import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import SiteListViewData from './siteListViewData';
import { useNavigate } from 'react-router-dom';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    name: 250, address: 250, postal_code: 250, city: 250, region: 250, phone_number: 250
};

const ListView = React.memo(({ scrollRef, sites, setOffset, sortBy, filteredSitesCount, setSitesFetched, setSortBy }) => {

    const navigate = useNavigate();
    const tableHeadRef = useRef(null);

    const [siteColumnWidth, setSiteColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('siteColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredSitesCount, setSitesFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setSitesFetched, filteredSitesCount, 70, 280);

    const handleSiteClick = useCallback((id) => navigate(`site_details/${id}`), [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const handleColumnWidthChange = (value, columnKey) => setSiteColumnWidth((prevState) => ({ ...prevState, [columnKey]: value }));

    useEffect(() => localStorage.setItem('siteColumnWidth', JSON.stringify(siteColumnWidth)), [siteColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { sites, handleSiteClick } = data;
        const site = index === 0 ? sites[0] : sites[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <SiteListViewData
                    site={site}
                    columnWidth={siteColumnWidth}
                    handleSiteClick={handleSiteClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    backgroundColor='#F6F6F6'
                    label='Name'
                    columnKey='name'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={siteColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    backgroundColor='#F6F6F6'
                    label='Adresse'
                    columnKey='address'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={false}
                    columnWidth={siteColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    backgroundColor='#F6F6F6'
                    label='Postal code'
                    columnKey='postal_code'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={false}
                    columnWidth={siteColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    backgroundColor='#F6F6F6'
                    label='Ville'
                    columnKey='city'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={false}
                    columnWidth={siteColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    backgroundColor='#F6F6F6'
                    label='Numéro Tel'
                    columnKey='phone_number'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={false}
                    columnWidth={siteColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    backgroundColor='#F6F6F6'
                    label='Region'
                    columnKey='region'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={false}
                    columnWidth={siteColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell className='table_header_cell_non_filter' width="50px" backgroundColor='#F6F6F6' />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredSitesCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredSitesCount}
                    itemSize={70}
                    itemData={{ sites, handleSiteClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default ListView;